import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import {
    fetchPatientsGetById,
    fetchPatientsUpdateClinicalContext,
} from '~/backend/connector-next-api/connector-next-components';
import { useConnectorNextContext } from '~/backend/connector-next-api/connector-next-context';
import {
    ConnectorNextError,
    connectorNextFetch,
} from '~/backend/connector-next-api/connector-next-fetcher';
import type {
    ImportInstancesResponse,
    ImportedPatientResult,
} from '~/backend/connector-next-api/connector-next-schemas';
import { errorMessage, type ImportCommit, type ImportOutcome } from '../model';

/** Committing a scanned drop: upload the whole drop in one request, then attach the clinical context. */

/** Normalizes one patient's wire result; every field of the generated DTO is optional. */
function toOutcome(dto: ImportedPatientResult): ImportOutcome | undefined {
    if (!dto.patientId) {
        return undefined;
    }
    return {
        patientId: dto.patientId,
        wasNewPatient: dto.wasNewPatient ?? false,
        instancesImported: dto.instancesImported ?? 0,
        instancesAlreadyPresent: dto.instancesAlreadyPresent ?? 0,
    };
}

/** More than one per-patient result means the wizard's partition and the server's disagree, so the context is not attributable. */
function multiPatientMessage(count: number) {
    return {
        id: 'patientList.import.error.multiPatient',
        values: { count },
    } as const;
}

const NO_RESULT_MESSAGE = {
    id: 'patientList.import.error.noResult',
} as const;

export function useImportCommit(): ImportCommit {
    const { fetcherOptions } = useConnectorNextContext();
    const queryClient = useQueryClient();
    // The context hook returns a fresh object every render; depending on it directly would rebuild commit each render.
    const fetcherOptionsRef = useRef(fetcherOptions);
    fetcherOptionsRef.current = fetcherOptions;

    return useCallback<ImportCommit>(
        async (instances, context, onPhase) => {
            try {
                const files = instances.map((instance) => instance.file);
                onPhase({ kind: 'uploading', totalFiles: files.length });

                const form = new FormData();
                for (const file of files) {
                    form.append('files', file, file.name);
                }

                let response: ImportInstancesResponse;
                try {
                    response = await connectorNextFetch<
                        ImportInstancesResponse,
                        unknown,
                        FormData,
                        { 'Content-Type': string },
                        {},
                        {}
                    >({
                        ...fetcherOptionsRef.current,
                        url: '/api/dicom/instances',
                        method: 'post',
                        body: form,
                        // The fetcher strips this header only when it names multipart, letting the browser set the boundary.
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });
                } catch (error) {
                    // A received status is definitive: the endpoint imports one request as one
                    // transaction, so a rejected one stored nothing. A transport failure is not:
                    // the server may have committed and the response been lost on the way back.
                    return {
                        ok: false,
                        message: errorMessage(
                            error,
                            'patientList.import.error.uploadFailed'
                        ),
                        landed: [],
                        landingUnknown: !(error instanceof ConnectorNextError),
                    };
                }

                const landed = (response.results ?? [])
                    .map(toOutcome)
                    .filter((o): o is ImportOutcome => o !== undefined);
                if (landed.length !== 1) {
                    return {
                        ok: false,
                        message:
                            landed.length === 0
                                ? NO_RESULT_MESSAGE
                                : multiPatientMessage(landed.length),
                        landed,
                        landingUnknown: false,
                    };
                }
                const outcome = landed[0];

                const hasContext = Object.values(context).some(
                    (value) => value != null && value !== ''
                );
                if (!hasContext) {
                    return { ok: true, outcome };
                }

                onPhase({ kind: 'saving-context' });
                try {
                    // Read the version after the upload: importing touches the patient, so a
                    // token taken before it is already stale.
                    const patient = await fetchPatientsGetById({
                        ...fetcherOptionsRef.current,
                        pathParams: { id: outcome.patientId },
                    });

                    await fetchPatientsUpdateClinicalContext({
                        ...fetcherOptionsRef.current,
                        body: {
                            treatmentSite: patient.treatmentSite ?? null,
                            notes: context.notes ?? patient.notes ?? null,
                            diagnosis: patient.diagnosis ?? null,
                            referringPhysician:
                                patient.referringPhysician ?? null,
                        },
                        headers: { 'If-Match': patient.version ?? '' },
                        pathParams: { id: outcome.patientId },
                    });
                } catch (error) {
                    return {
                        ok: false,
                        // The frame is translated; only the server's own reason passes through verbatim.
                        message:
                            error instanceof Error && error.message
                                ? {
                                      id: 'patientList.import.error.contextNotSavedBecause',
                                      values: { reason: error.message },
                                  }
                                : {
                                      id: 'patientList.import.error.contextNotSaved',
                                  },
                        landed,
                        // The upload itself was answered; only the context write was not.
                        landingUnknown: false,
                    };
                }

                return { ok: true, outcome };
            } finally {
                // In a finally so no exit path leaves the worklist showing pre-import data; fired not awaited to keep the modal closeable.
                void queryClient.invalidateQueries({
                    queryKey: ['api', 'patients'],
                });
            }
        },
        [queryClient]
    );
}
